<script setup lang="ts">
definePageMeta({
  layout: 'default',
  hideDefaultTopNav: true,
})

import {
  getMediationRankVisibility,
  invokeExposureRoute,
  normalizeExposureRevelationData,
  type ExposureRevelationResponse,
  REVIEW_REVELATION_ROUTES,
} from '~/api/exposure'

const route = useRoute()
const REVELATION_PAGE_SIZE = 20

/** reviews 详情页走独立内容，列表页复用曝光频道外层壳子。 */
const isReviewDetailRoute = computed(() => {
  return typeof route.params.review_id === 'string' && !!route.params.review_id
})

/** 当前 reviews 路由的分类别名。 */
const reviewCategoryAlias = computed(() => {
  const category = route.params.category
  const alias = Array.isArray(category) ? category[0] : category
  return typeof alias === 'string' && alias.trim() ? alias.trim() : ''
})

/** 显式访问第一页分页地址时直接视为 NotFound。 */
const isExplicitFirstPageRoute = computed(() => /\/page\/1(?:\/|$)/.test(route.path))

/** 仅为分类路由校验 alias；根列表不依赖分类接口决定 404。 */
const fetchReviewCategories = async () => {
  if (!reviewCategoryAlias.value) {
    return []
  }

  try {
    const response = await invokeExposureRoute<ExposureRevelationResponse>(REVIEW_REVELATION_ROUTES, {
      userid: '',
      keyword: '',
      pageIndex: 1,
      pageSize: REVELATION_PAGE_SIZE,
      negativeType: '0',
    })

    return normalizeExposureRevelationData(response).categories
  } catch {
    return []
  }
}

const { data: reviewCategories } = await useAsyncData(
  () => `reviews-category:${reviewCategoryAlias.value}`,
  fetchReviewCategories,
  {
    default: () => [],
    watch: [reviewCategoryAlias],
  },
)

/** 分类集合明确返回后才校验 alias，避免语言切换时空分类误判为 404。 */
const hasInvalidReviewCategory = computed(() => {
  return (
    !!reviewCategoryAlias.value &&
    reviewCategories.value.length > 0 &&
    !reviewCategories.value.some((item) => item.alias === reviewCategoryAlias.value)
  )
})

const isReviewsNotFound = computed(() => {
  return !isReviewDetailRoute.value && (isExplicitFirstPageRoute.value || hasInvalidReviewCategory.value)
})

if (import.meta.server && isReviewsNotFound.value) {
  setResponseStatus(404)
}

/** 仅在 reviews 列表页 SSR 拉取调解中心 tab 展示状态。 */
const showMediationTab = ref(true)

if (!isReviewDetailRoute.value) {
  const { data } = await useAsyncData('reviews-mediation-tab-visible', async () => {
    return await getMediationRankVisibility()
  })
  showMediationTab.value = data.value !== false
}
</script>

<template>
  <HeaderWikiBitPcTopNav :always-expand-search="true" :force-solid-background="true" />

  <NuxtPage v-if="isReviewDetailRoute" />

  <CommonNotFound v-else-if="isReviewsNotFound" />

  <ExposureShell v-else :show-mediation-tab="showMediationTab">
    <ReviewsReviewExposureListContent :prefetched-categories="reviewCategories" />
  </ExposureShell>
</template>
